import React from 'react';
import { Button } from '@components/ui/button';
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle
} from '@components/ui/card';
import { Label } from '@components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from '@components/ui/select';
import LocalizedTextArea from '@components/text-area/localized-text-area';
import LocalizedTextField from '@components/textfield/localized_textfield';
import {
    CatalogAppDto,
    I18NText,
    UpdateCatalogAppDto
} from '@core/api/MobileClient';

type Category = NonNullable<CatalogAppDto['category']>;

const CATEGORIES: { value: Category; label: string }[] = [
    { value: 'HABITS', label: 'Habits' },
    { value: 'TRAINING', label: 'Training' },
    { value: 'EXPLORE', label: 'Explore' },
    { value: 'SOCIAL', label: 'Social' }
];

type Draft = { name: I18NText; description: I18NText; category?: Category };

const draftOf = (app?: CatalogAppDto): Draft => ({
    name: { ...app?.name },
    description: { ...app?.description },
    category: app?.category
});

export function ListingCard({
    catalogApp,
    busy,
    onSave
}: {
    catalogApp?: CatalogAppDto;
    busy: boolean;
    onSave: (listing: UpdateCatalogAppDto) => Promise<void>;
}) {
    const [draft, setDraft] = React.useState<Draft>(() => draftOf(catalogApp));

    React.useEffect(() => setDraft(draftOf(catalogApp)), [catalogApp]);

    return (
        <Card>
            <CardHeader>
                <CardTitle>Store listing</CardTitle>
                <CardDescription>
                    Name, "About this app" and category on the app's store page.
                    A blank line in the description splits what shows before
                    "more". An empty language falls back to the copy built into
                    the app.
                </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col gap-4">
                <div className="flex max-w-xs flex-col gap-2">
                    <Label>Category</Label>
                    <Select
                        value={draft.category}
                        disabled={busy || !catalogApp}
                        onValueChange={(category) =>
                            setDraft((current) => ({
                                ...current,
                                category: category as Category
                            }))
                        }
                    >
                        <SelectTrigger>
                            <SelectValue placeholder="No category" />
                        </SelectTrigger>
                        <SelectContent>
                            {CATEGORIES.map((category) => (
                                <SelectItem
                                    key={category.value}
                                    value={category.value}
                                >
                                    {category.label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                <LocalizedTextField
                    type="text"
                    label="Name"
                    value={draft.name}
                    disabled={busy || !catalogApp}
                    onChange={(name) =>
                        setDraft((current) => ({ ...current, name }))
                    }
                />
                <LocalizedTextArea
                    label="About this app"
                    value={draft.description}
                    disabled={busy || !catalogApp}
                    onChange={(description) =>
                        setDraft((current) => ({ ...current, description }))
                    }
                />

                <div>
                    <Button
                        disabled={busy || !catalogApp}
                        onClick={() => void onSave(draft)}
                    >
                        Save listing
                    </Button>
                </div>
            </CardContent>
        </Card>
    );
}
